import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, NgModule, OnInit, inject } from '@angular/core';
import { SignOffStepCardComponent } from '@data-terminal/feature-dialogs/components/sign-off-card-component.abstract';
import { OperationActionSignOff, SignOffOperation } from '@data-terminal/shared-models/dialogs/sign-off-dialog';
import { BehaviorSubject, Subject } from 'rxjs';
import { OperationService } from '@data-terminal/feature-workstation-details/services/operation/operation.service';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { requireMinValueOptional, requireOneControl } from '@data-terminal/utils/form';
import { MatRadioModule } from '@angular/material/radio';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { CustomDividerModule } from '@data-terminal/ui-presentational/components';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FeatureOperationServiceModule } from '@data-terminal/feature-workstation-details/operation-service.module';
import { AmountReportDetailsModule } from '@data-terminal/feature-workstation-details/components/amount-report-details/amount-report-details.component';
import { NgxMaskDirective } from 'ngx-mask';
import {
    AmountDialogFormData,
    OperationAmountDetails,
} from '@data-terminal/feature-workstation-details/models/amount-report-details.model';

@UntilDestroy()
@Component({
    selector: 'data-terminal-sign-off-operation-card',
    templateUrl: './sign-off-operation-card.component.html',
    styleUrls: ['./sign-off-operation-card.component.scss'],
    standalone: false,
})
export class SignOffOperationCardComponent extends SignOffStepCardComponent<SignOffOperation> implements OnInit {
    private readonly operationService = inject(OperationService);
    readonly #translateService = inject(TranslateService);

    public PAUSE = OperationActionSignOff.PAUSE;
    public FINISH = OperationActionSignOff.FINISH;

    public formData$ = new BehaviorSubject<AmountDialogFormData>({
        goodAmount: 0,
        wasteAmount: 0,
    });
    public operationData$ = new Subject<OperationAmountDetails>();

    public get translatedOpName(): string {
        const opName = this.machine.runningOperation?.opName?.trim() ?? '';
        return opName === 'UnplannedWork' ? this.#translateService.instant('AP_UNSCHEDULED_OPERATION') : opName || '-';
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.getRunningOperationData();
    }

    public onInputOut(): void {
        this.formGroup.markAllAsTouched();
    }

    public override createFormGroup(): void {
        this.formGroup = new UntypedFormGroup(
            {
                goodCount: new UntypedFormControl(),
                wasteCount: new UntypedFormControl(),
                comment: new UntypedFormControl(),
                operationAction: new UntypedFormControl(this.FINISH),
                runningOperation: new UntypedFormControl(this.machine.runningOperation?.primaryKey),
            },
            [
                requireOneControl('goodCount', 'wasteCount'),
                requireMinValueOptional('goodCount', 0),
                requireMinValueOptional('wasteCount', 0),
            ]
        );
        this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe((data) => {
            this.formData$.next({
                goodAmount: data.goodCount || 0,
                wasteAmount: data.wasteCount || 0,
            });
        });
    }

    public getRunningOperationData(): void {
        if (this.machine.runningOperation !== null && this.machine.runningOperation !== undefined) {
            this.operationService
                .getOperation(this.machine.runningOperation.primaryKey)
                .pipe(untilDestroyed(this))
                .subscribe((runningOperation) => {
                    this.operationData$.next({
                        currentGood: runningOperation.opCtrGood,
                        plannedGood: runningOperation.opPlannedGoodCtr,
                        currentWaste: runningOperation.opCtrWaste,
                        plannedWaste: runningOperation.opPlannedWasteCtr,
                    });
                });
        }
    }
}

@NgModule({
    declarations: [SignOffOperationCardComponent],
    exports: [SignOffOperationCardComponent],
    imports: [
        MatRadioModule,
        CommonModule,
        MatFormFieldModule,
        MatSelectModule,
        MatCardModule,
        CustomDividerModule,
        MatDividerModule,
        MatInputModule,
        ReactiveFormsModule,
        TranslateModule,
        MatProgressBarModule,
        FeatureOperationServiceModule,
        AmountReportDetailsModule,
        NgxMaskDirective,
    ],
})
export class SignOffOperationCardModule {}
